import { useRef, useState } from "react";
import { UseFormSetValue, UseFormWatch } from "react-hook-form";
import { HTTP_STATUSES } from "@miplatform/constants";
import {
  externalReferenceId,
  getDocumentTypeByContentType,
  sortOnField,
} from "@miplatform/utils";
import {
  useAttributes,
  useCategories,
  useDocuments,
  useMaterials,
} from "@mibuildweb/hooks";
import { IItemAttribute, IItemData, IItemDocument } from "../../Items.types";

export const useItemDetailTabViewModel = ({
  setValue,
  watch,
}: IUseItemDetailTabViewModelProps) => {
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [attributeOptions, setAttributeOptions] = useState<{
    [attributeId: string]: Array<{ label: string; value: string }>;
  }>({});
  const [category, setCategory] = useState<any>([]);

  const imageRef = useRef<HTMLInputElement>(null);
  const { id, document: { id: documentId, modifiedOn } = {} as IItemDocument } =
    watch();

  const {
    isLoading: isCategoriesLoading,
    getCategories,
    getCategoryDetails,
  } = useCategories();
  const { isLoading: isAttributeLoading, getAttributeSearchOptions } =
    useAttributes();
  const {
    isLoading: isDocumentLoading,
    createDocument,
    deleteDocument,
  } = useDocuments();
  const { getMaterialPatternAutoGenerator } = useMaterials();

  const fetchCategoryDetails = async (id: string) => {
    const response = await getCategoryDetails(id);
    if (response.status === HTTP_STATUSES.OK) {
      response.data.attributes?.sort(sortOnField("displayOrder"));
      response.data.attributes?.forEach(
        (attr: IItemAttribute, index: number) => {
          setValue(`attributes.${index}.isRequired`, attr.isRequired);
          setValue(`attributes.${index}.attributeName`, attr.name);
        }
      );
      setCategory(response.data);
      setValue(`category.imageRequired`, response.data.imageRequired ?? false);
      setValue("category.costCalculation", response.data.costCalculation);
      setValue("category.multiplier", response.data.multiplier);
    }
  };

  const getCategoryOptions = async () => {
    const response = await getCategories();
    if (response.status === HTTP_STATUSES.OK) {
      const options = response.data.map((o: { name: string; id: string }) => ({
        label: o.name,
        value: o.id,
      }));
      setCategoryOptions(options);
    }
  };

  const getAttributeOptions = async (
    attributeId: string,
    categoryId: string,
    searchText?: string
  ) => {
    const response = await getAttributeSearchOptions(
      attributeId,
      categoryId,
      searchText
    );
    if (response.status === HTTP_STATUSES.OK) {
      setAttributeOptions((prevOptions) => ({
        ...prevOptions,
        [attributeId]: response.data,
      }));
    }
  };

  const handleUploadImage = async (file: File) => {
    const params = {
      title: file.name,
      documentType: getDocumentTypeByContentType(file.type),
      externalReferenceId: externalReferenceId.getMaterialRefId({
        materialId: id,
      }),
      applyWatermark: false,
    };
    if (imageRef.current) {
      imageRef.current.value = "";
    }
    const { data, status } = await createDocument({
      data: file,
      params,
      contentType: file.type,
    });
    if (status === HTTP_STATUSES.OK) {
      setValue("document", data, { shouldValidate: true });
    }
  };

  const deleteImage = async () => {
    const response = await deleteDocument({
      id: documentId as string,
      modifiedOn,
    });
    if (response.status === HTTP_STATUSES.OK) {
      setValue("document", {} as any, { shouldValidate: true });
    }
    return response;
  };

  const handleUpdateImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (!files || files.length === 0) {
      return;
    }
    const response = await deleteImage();
    if (response.status === HTTP_STATUSES.OK) {
      handleUploadImage(files[0]);
    }
  };

  const getAutoGenerate = async (data: IItemData) => {
    const modifiedData = {
      ...data,
      attributes: data.attributes.filter((attribute) => attribute.value),
    };
    const response = await getMaterialPatternAutoGenerator(modifiedData);
    if (response.status === HTTP_STATUSES.OK) {
      setValue(`category.itemCode`, response.data.itemCode, {
        shouldValidate: true,
      });
      setValue(`category.itemDescription`, response.data.itemDescription, {
        shouldValidate: true,
      });
    }
  };

  return {
    isLoading: isCategoriesLoading || isAttributeLoading || isDocumentLoading,
    category,
    categoryOptions,
    attributeOptions,
    imageRef,
    setCategory,
    fetchCategoryDetails,
    getCategoryOptions,
    getAttributeOptions,
    deleteImage,
    handleUploadImage,
    handleUpdateImage,
    getAutoGenerate,
  };
};

interface IUseItemDetailTabViewModelProps {
  watch: UseFormWatch<IItemData>;
  setValue: UseFormSetValue<IItemData>;
}
